import { useEffect, useState } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Receipt, TrendingUp, TrendingDown, Plus, Wallet, Clock, CheckCircle2, Layers } from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { useAuth } from "../../context/AuthContext";
import { DatePicker, MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `/finance/utang-beban` dan `/finance/dibayar-dimuka` sama-sama mengurut
// `created_at desc` lalu memotong `to_list(500)` di backend TANPA parameter
// urut dari klien (lihat transactions.py) — sort di sini murni client-side,
// wajib didampingi `BatasDaftarNotice` (lihat ./_shared.jsx).
const BEBAN_BATAS_SERVER = 500;

const COLORS = ["#EF4444", "#F97316", "#F59E0B", "#84CC16", "#059669", "#0EA5E9", "#8B5CF6", "#EC4899"];

const TABS = [
  { key: "ringkasan", label: "Ringkasan", icon: Receipt, mod: "finance_beban" },
  { key: "utang", label: "Utang Beban", icon: Clock, mod: "finance_catat" },
  { key: "ddm", label: "Dibayar di Muka", icon: Layers, mod: "finance_catat" },
];

export default function FinanceBeban() {
  const { can } = useAuth();
  const [tab, setTab] = useState("ringkasan");
  const [showForm, setShowForm] = useState(false);
  // Pemicu muat ulang seksi Utang Beban / Dibayar di Muka setelah catat baru,
  // pembayaran, atau amortisasi — supaya daftarnya langsung sinkron tanpa
  // harus pindah tab lalu balik lagi.
  const [reload, setReload] = useState(0);
  const bump = () => setReload(n => n + 1);

  const visibleTabs = TABS.filter(t => can(t.mod, "view"));

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-beban">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Beban Operasional</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Beban Usaha</h1>
          <p className="text-sm text-slate-600 mt-1">Ringkasan, utang beban yang belum dibayar, dan beban dibayar di muka.</p>
        </div>
        {can("finance_catat", "create") && (
          <button onClick={() => setShowForm(true)} data-testid="beban-new" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Catat Beban
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
        {visibleTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`beban-tab-${t.key}`}
              className={`px-4 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition ${tab === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "ringkasan" && can("finance_beban", "view") && <RingkasanBeban />}
      {tab === "utang" && can("finance_catat", "view") && <UtangBebanSection can={can} onChanged={bump} reloadKey={reload} />}
      {tab === "ddm" && can("finance_catat", "view") && <DibayarDimukaSection can={can} onChanged={bump} reloadKey={reload} />}

      {showForm && <FormCatatBeban onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); bump(); }} />}
    </div>
  );
}

/* ================ RINGKASAN (grafik & breakdown per kategori) ==================== */
function RingkasanBeban() {
  const [periode, setPeriode] = useState(bulanIniISO());
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async (p) => {
    setLoading(true);
    setLoadError(null);
    try {
      const y = p.slice(0, 4), m = p.slice(5, 7);
      const pMon = Number(m) - 1; let py = y, pm = pMon;
      if (pm <= 0) { pm = 12; py = String(Number(y) - 1); }
      const prevP = `${py}-${String(pm).padStart(2, "0")}`;
      const [cur, pv] = await Promise.all([
        api.get(`/finance/beban/summary?periode=${p}`),
        api.get(`/finance/beban/summary?periode=${prevP}`),
      ]);
      setData(cur.data); setPrev(pv.data);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat beban"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(periode); }, [periode]);

  const prevMap = {};
  (prev?.breakdown || []).forEach(b => { prevMap[b.akun_code] = b.total; });
  const deltaPct = data && prev?.grand_total > 0 ? ((data.grand_total - prev.grand_total) / prev.grand_total * 100) : 0;

  return (
    <LoadingState loading={loading} error={loadError} onRetry={() => load(periode)} varian="kartu" jumlahKartu={3} data-testid="loading-beban-ringkasan">
    {data && <div className="space-y-6">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <p className="text-sm text-slate-600">Periode {periode} · {data.kategori_count} kategori</p>
        <MonthPicker value={periode} onChange={setPeriode} data-testid="beban-periode" className="w-36" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Bulan Ini</p>
          <p className="font-display text-2xl font-bold text-rose-700 mt-2">{idr(data.grand_total)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bulan Lalu</p>
          <p className="font-display text-2xl font-bold text-slate-700 mt-2">{idr(prev?.grand_total || 0)}</p>
        </div>
        <div className={`border rounded-xl p-5 shadow-sm ${deltaPct > 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${deltaPct > 0 ? "text-rose-700" : "text-emerald-700"}`}>Perubahan</p>
          <p className={`font-display text-2xl font-bold mt-2 flex items-center gap-2 ${deltaPct > 0 ? "text-rose-800" : "text-emerald-800"}`}>
            {deltaPct > 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
            {deltaPct.toFixed(1)}%
          </p>
          <p className={`text-[11px] mt-1 ${deltaPct > 0 ? "text-rose-700" : "text-emerald-700"}`}>{deltaPct > 0 ? "Naik dari bulan lalu" : "Turun dari bulan lalu"}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="font-display font-bold text-base text-slate-900">Rincian per Kategori</p>
          <p className="text-xs text-slate-500 mt-0.5">Diurut dari terbesar. Bar menunjukkan porsi terhadap total.</p>
        </div>
        <div className="p-3">
          {data.breakdown.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Belum ada beban di periode ini</div>
          ) : (
            <div className="space-y-2">
              {data.breakdown.map((b, i) => {
                const pctPrev = prevMap[b.akun_code];
                const delta = pctPrev ? ((b.total - pctPrev) / pctPrev * 100) : null;
                return (
                  <div key={b.akun_code} data-testid={`beban-cat-${b.akun_code}`} className="px-3 py-2.5 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <p className="text-sm font-medium text-slate-800">{b.akun_name}</p>
                        <span className="text-[10px] text-slate-400 font-mono">{b.akun_code}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {delta !== null && (
                          <span className={`text-[10px] font-semibold ${delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                            {delta > 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
                          </span>
                        )}
                        <p className="text-sm font-mono font-semibold text-slate-900 min-w-[100px] text-right">{idrShort(b.total)}</p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div style={{ width: `${b.pct}%`, background: COLORS[i % COLORS.length] }} className="h-full rounded-full" />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">{b.pct}% dari total · {b.count} transaksi</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>}
    </LoadingState>
  );
}

/* ================ SEKSI: Utang Beban (Finance #11) ==================== */
function UtangBebanSection({ can, onChanged, reloadKey }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [totalAktif, setTotalAktif] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [bayarFor, setBayarFor] = useState(null);
  const canBayar = can("finance_catat", "update");
  // Void utang beban = jurnal balik D 2300 / C beban asal — gerbang role-nya
  // mengikuti backend (POST /finance/utang-beban/{id}/void), bukan can().
  const bisaVoid = ["finance_spv", "finance_ceo", "admin", "superadmin"].includes(user?.role);

  const voidUb = async (u) => {
    const reason = window.prompt(`Alasan membatalkan utang beban ${u.kategori} ${idr(u.nominal)}? (salah input, dll)`);
    if (!reason) return;
    try {
      const r = await api.post(`/finance/utang-beban/${u.id}/void`, { reason });
      toast.success(r.data.message || "Utang beban dibatalkan ✓");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/utang-beban");
      setItems(r.data.items || []);
      setTotalAktif(r.data.total_aktif || 0);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat utang beban"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reloadKey]);

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kode: (u) => u.code || "",
    kategori: (u) => u.kategori || "",
    nominal: (u) => u.nominal || 0,
    sisa: (u) => (u.nominal || 0) - (u.nominal_terbayar || 0),
  });

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 grid place-items-center shrink-0"><Clock className="w-5 h-5" /></div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">Total Utang Beban Aktif (belum dibayar)</p>
            <p className="font-display text-2xl font-bold text-rose-900 mt-1">{idr(totalAktif)}</p>
          </div>
        </div>
      </div>

      <BatasDaftarNotice shown={items.length} batas={BEBAN_BATAS_SERVER} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Kode / Tanggal" sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="ub-sort-kode" />
                <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="ub-sort-kategori" />
                <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="ub-sort-nominal" />
                <SortTh label="Sisa" sortKeyName="sisa" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="ub-sort-sisa" />
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada utang beban tercatat" varian="tabel" kolom={6} data-testid="loading-utang-beban">
              {sortedItems.map(u => {
                const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
                return (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-slate-500">{u.code}</p>
                      <p className="text-xs text-slate-600">{formatTanggal(u.tanggal)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{u.kategori}</p>
                      {u.deskripsi && <p className="text-[10px] text-slate-500 truncate max-w-[220px]" title={u.deskripsi}>{u.deskripsi}</p>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-800">{idr(u.nominal)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(sisa)}</td>
                    <td className="px-4 py-3 text-center">
                      {u.status === "lunas" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Lunas</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-sky-50 text-sky-700 border-sky-200"><Clock className="w-3 h-3" /> Aktif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        {u.status === "aktif" && canBayar && (
                          <button onClick={() => setBayarFor(u)} data-testid={`ub-bayar-${u.id}`} className="text-xs font-medium text-rose-700 hover:underline">Bayar</button>
                        )}
                        {u.status === "aktif" && bisaVoid && (
                          <button onClick={() => voidUb(u)} data-testid={`ub-void-${u.id}`} title="Batalkan (jurnal balik) — hanya bila belum ada pembayaran"
                            className="text-xs font-medium text-slate-500 hover:text-rose-700 hover:underline">Batalkan</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              </LoadingState>
            </tbody>
          </table>
        </div>
      </div>

      {bayarFor && <FormBayarUtangBeban ub={bayarFor} onClose={() => setBayarFor(null)} onSaved={() => { setBayarFor(null); onChanged(); }} />}
    </div>
  );
}

function FormBayarUtangBeban({ ub, onClose, onSaved }) {
  const sisa = (ub.nominal || 0) - (ub.nominal_terbayar || 0);
  const [kasBank, setKasBank] = useState([]);
  const [form, setForm] = useState({ tanggal: hariIniISO(), nominal: sisa, sumber_akun_id: "", keterangan: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/finance/accounts/kas-bank").then(r => setKasBank(r.data || [])).catch(() => toast.error("Gagal memuat daftar rekening")); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/utang-beban/${ub.id}/bayar`, { ...form, nominal: Number(form.nominal) });
      toast.success(r.data.message || "Pembayaran tercatat ✓");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal bayar"); }
    finally { setBusy(false); }
  };

  // `dangerous` — nominal pembayaran diketik/dipilih staf sendiri (default
  // dari sisa, tapi bisa diubah), plus pilihan rekening sumber dana.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || !form.sumber_akun_id || !form.nominal, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="ub-bayar-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Wallet className="w-5 h-5 text-rose-600" /> Bayar Utang Beban</h3>
        <p className="text-sm text-slate-600 mb-4">{ub.kategori} · Sisa {idr(sisa)}</p>
        <div className="space-y-3">
          <Fld label="Tanggal Bayar"><DatePicker value={form.tanggal} onChange={v => setForm({ ...form, tanggal: v })} data-testid="ub-bayar-tanggal" /></Fld>
          <Fld label="Nominal Bayar"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="in" testid="ub-bayar-nom" /></Fld>
          <Fld label="Bayar Dari Rekening">
            <select value={form.sumber_akun_id} onChange={e => setForm({ ...form, sumber_akun_id: e.target.value })} className="in" data-testid="ub-bayar-akun">
              <option value="">Pilih rekening / kas...</option>
              {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} · saldo {idr(a.balance)}</option>)}
            </select>
          </Fld>
          <Fld label="Keterangan (opsional)"><input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="in" /></Fld>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="ub-bayar-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !form.sumber_akun_id || !form.nominal} data-testid="ub-bayar-submit" className="flex-1 h-10 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Bayar"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #E11D48; box-shadow: 0 0 0 3px rgba(225,29,72,0.1); }`}</style>
      </div>
    </div>
  );
}

/* ================ SEKSI: Dibayar di Muka (Finance #11) ==================== */
function DibayarDimukaSection({ can, onChanged, reloadKey }) {
  const [items, setItems] = useState([]);
  const [totalSisa, setTotalSisa] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [amortFor, setAmortFor] = useState(null);
  const canAmort = can("finance_catat", "update");

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/dibayar-dimuka");
      setItems(r.data.items || []);
      setTotalSisa(r.data.total_sisa || 0);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat beban dibayar di muka"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reloadKey]);

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kode: (dd) => dd.code || "",
    kategori: (dd) => dd.kategori || "",
    nominal: (dd) => dd.nominal || 0,
    teramortisasi: (dd) => dd.nominal_teramortisasi || 0,
    sisa: (dd) => (dd.nominal || 0) - (dd.nominal_teramortisasi || 0),
  });

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 grid place-items-center shrink-0"><Layers className="w-5 h-5" /></div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">Sisa Aset Beban Dibayar di Muka (akun 1250)</p>
            <p className="font-display text-2xl font-bold text-sky-900 mt-1">{idr(totalSisa)}</p>
          </div>
        </div>
      </div>

      <BatasDaftarNotice shown={items.length} batas={BEBAN_BATAS_SERVER} />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Kode / Tanggal" sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="ddm-sort-kode" />
                <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="ddm-sort-kategori" />
                <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="ddm-sort-nominal" />
                <SortTh label="Teramortisasi" sortKeyName="teramortisasi" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="ddm-sort-teramortisasi" />
                <SortTh label="Sisa" sortKeyName="sisa" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="ddm-sort-sisa" />
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada beban dibayar di muka" varian="tabel" kolom={7} data-testid="loading-ddm">
              {sortedItems.map(dd => {
                const sisa = (dd.nominal || 0) - (dd.nominal_teramortisasi || 0);
                const bisaAmort = canAmort && dd.status === "posted" && dd.amortisasi_status === "aktif";
                return (
                  <tr key={dd.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-slate-500">{dd.code}</p>
                      <p className="text-xs text-slate-600">{formatTanggal(dd.tanggal)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{dd.kategori}</p>
                      {dd.amortisasi_bulan && <p className="text-[10px] text-slate-500">Rencana {dd.amortisasi_bulan} bulan</p>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-800">{idr(dd.nominal)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{idr(dd.nominal_teramortisasi || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{idr(sisa)}</td>
                    <td className="px-4 py-3 text-center">
                      {dd.status === "pending_approval" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">Pending SPV</span>
                      ) : dd.status === "voided" || dd.status === "rejected" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-100 text-slate-500 border-slate-200">{dd.status === "voided" ? "Dibatalkan" : "Ditolak"}</span>
                      ) : dd.amortisasi_status === "lunas" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Lunas</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-sky-50 text-sky-700 border-sky-200"><Clock className="w-3 h-3" /> Aktif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {bisaAmort && (
                        <button onClick={() => setAmortFor(dd)} data-testid={`ddm-amortisasi-${dd.id}`} className="text-xs font-medium text-sky-700 hover:underline">Amortisasi</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              </LoadingState>
            </tbody>
          </table>
        </div>
      </div>

      {amortFor && <FormAmortisasi dd={amortFor} onClose={() => setAmortFor(null)} onSaved={() => { setAmortFor(null); onChanged(); }} />}
    </div>
  );
}

function FormAmortisasi({ dd, onClose, onSaved }) {
  const sisa = (dd.nominal || 0) - (dd.nominal_teramortisasi || 0);
  const [form, setForm] = useState({ tanggal: hariIniISO(), nominal: "", keterangan: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/dibayar-dimuka/${dd.id}/amortisasi`, { ...form, nominal: Number(form.nominal) });
      toast.success(r.data.message || "Amortisasi tercatat ✓");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal amortisasi"); }
    finally { setBusy(false); }
  };

  // `dangerous` — nominal amortisasi diketik staf sendiri, tidak ada default aman.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || !form.nominal, dangerous: true });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="ddm-amort-modal" className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Layers className="w-5 h-5 text-sky-600" /> Amortisasi Dibayar di Muka</h3>
        <p className="text-sm text-slate-600 mb-4">{dd.kategori} · Sisa {idr(sisa)} dari total {idr(dd.nominal)}</p>
        <div className="space-y-3">
          <Fld label="Tanggal"><DatePicker value={form.tanggal} onChange={v => setForm({ ...form, tanggal: v })} data-testid="ddm-amort-tanggal" /></Fld>
          <Fld label="Nominal Diamortisasi"><InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="in" testid="ddm-amort-nom" /></Fld>
          <Fld label="Keterangan (opsional)"><input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} className="in" /></Fld>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="ddm-amort-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy || !form.nominal} data-testid="ddm-amort-submit" className="flex-1 h-10 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Amortisasi"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #0284C7; box-shadow: 0 0 0 3px rgba(2,132,199,0.1); }`}</style>
      </div>
    </div>
  );
}

/* ================ FORM: Catat Beban (Finance #11 — status_bayar & dibayar_dimuka) ==================== */
function FormCatatBeban({ onClose, onSaved }) {
  const [kasBank, setKasBank] = useState([]);
  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [f, setF] = useState({
    tanggal: hariIniISO(),
    kategori: "",
    nominal: "",
    sumber_akun_id: "",
    statusBayar: "lunas", // "lunas" | "utang"
    dibayarDimuka: false,
    amortisasiBulan: "",
    brand: "",
    deskripsi: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/finance/accounts/kas-bank"),
      api.get("/finance/kategori-options"),
    ]).then(([a, b]) => {
      setKasBank(a.data || []);
      // Filter kategori yang mengarah ke akun beban / HPP (5xxx/6xxx), sama seperti
      // form Catat Cepat > Beban Usaha supaya pilihannya konsisten di seluruh app.
      const opts = (b.data.uang_keluar_options || []).filter(o => {
        const code = String(o.coa_code || "");
        return code.startsWith("6") || code.startsWith("5");
      });
      setKategoriOpts(opts);
      setBrands(b.data.brands || []);
    }).catch(() => toast.error("Gagal memuat data"));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        tanggal: f.tanggal,
        jenis: "uang_keluar",
        kategori: f.kategori,
        nominal: Number(f.nominal),
        status_bayar: f.statusBayar,
        brand: f.brand || undefined,
        deskripsi: f.deskripsi || undefined,
      };
      if (f.statusBayar === "lunas") {
        payload.sumber_akun_id = f.sumber_akun_id;
        if (f.dibayarDimuka) {
          payload.dibayar_dimuka = true;
          if (f.amortisasiBulan) payload.amortisasi_bulan = Number(f.amortisasiBulan);
        }
      }
      const r = await api.post("/finance/transactions", payload);
      toast.success(r.data.message || "Beban tercatat ✓");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };

  const disableSubmit = busy || !f.kategori || !f.nominal || (f.statusBayar === "lunas" && !f.sumber_akun_id);

  // `dangerous` — form panjang (9 field) termasuk nominal yang diketik staf
  // sendiri; kehilangannya lewat klik/Escape tak sengaja berarti mengulang semua.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: disableSubmit, dangerous: true });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="beban-form-modal" className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-600" /> Catat Beban Usaha</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Tanggal"><DatePicker value={f.tanggal} onChange={v => setF({ ...f, tanggal: v })} data-testid="beban-tanggal" /></Fld>
            <Fld label="Nominal"><InputRupiah value={f.nominal} onChange={n => setF({ ...f, nominal: n })} testid="beban-nom" className="in" placeholder="0" /></Fld>
          </div>
          <Fld label="Kategori Beban">
            <select value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })} data-testid="beban-kat" className="in">
              <option value="">Pilih kategori beban...</option>
              {kategoriOpts.map(o => <option key={o.kategori} value={o.kategori}>{o.kategori}{o.source === "custom" ? " (custom)" : ""}</option>)}
            </select>
          </Fld>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Status Bayar</label>
            <div className="grid grid-cols-2 gap-2" data-testid="beban-status-bayar">
              <button type="button" onClick={() => setF({ ...f, statusBayar: "lunas" })}
                className={`h-10 rounded-lg border-2 text-sm font-medium ${f.statusBayar === "lunas" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                Lunas (uang keluar sekarang)
              </button>
              <button type="button" onClick={() => setF({ ...f, statusBayar: "utang", dibayarDimuka: false })}
                className={`h-10 rounded-lg border-2 text-sm font-medium ${f.statusBayar === "utang" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                Utang (bayar nanti)
              </button>
            </div>
          </div>

          {f.statusBayar === "utang" ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              Beban ini akan tercatat sebagai <b>Utang Beban</b> — kas TIDAK berkurang sekarang. Lunasi lewat tab Utang Beban di halaman ini kapan saja.
            </div>
          ) : (
            <Fld label="Dana Keluar Dari">
              <select value={f.sumber_akun_id} onChange={e => setF({ ...f, sumber_akun_id: e.target.value })} data-testid="beban-src" className="in">
                <option value="">Pilih rekening / kas...</option>
                {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} · saldo {idr(a.balance)}</option>)}
              </select>
            </Fld>
          )}

          {f.statusBayar === "lunas" && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <input type="checkbox" checked={f.dibayarDimuka} onChange={e => setF({ ...f, dibayarDimuka: e.target.checked })} data-testid="beban-ddm" className="w-4 h-4 accent-emerald-600" />
                Dibayar di muka (amortisasi manual)
              </label>
              {f.dibayarDimuka && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-slate-500">Uang keluar sekarang, dicatat sebagai aset (1250) — dipindah ke beban belakangan lewat tab Dibayar di Muka.</p>
                  <Fld label="Rencana Amortisasi (bulan, opsional)">
                    <input type="number" min="1" value={f.amortisasiBulan} onChange={e => setF({ ...f, amortisasiBulan: e.target.value })} placeholder="mis. 12" data-testid="beban-ddm-bulan" className="in" />
                  </Fld>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Fld label="Brand (opsional)">
              <select value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })} className="in">
                <option value="">-</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Fld>
            <Fld label="Deskripsi (opsional)">
              <input value={f.deskripsi} onChange={e => setF({ ...f, deskripsi: e.target.value })} placeholder="Untuk apa..." className="in" />
            </Fld>
          </div>
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="beban-form-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={disableSubmit} data-testid="beban-submit" className="flex-1 h-10 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }`}</style>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
